import { Input, Label } from "@b2b/ui-components";
import { LucideIcon } from "lucide-react";
import React from "react";
import { cn } from "../../../lib/utils";

/** Auth card (login / signup step 0) vs rest of app */
export type FormInputVisualVariant = "default" | "authSignin" | "authSignupEmail";

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon: LucideIcon;
  /** Renders inside the input row (e.g. password visibility toggle). */
  endAdornment?: React.ReactNode;
  /** Pixel-polished styles for /login only; default keeps existing app forms unchanged. */
  visualVariant?: FormInputVisualVariant;
}


const authCardInputBorderFocusClassName="focus:border-orange-500 focus-visible:border-orange-500 focus-visible:ring-orange-500/50 focus-visible:ring-[3px]"
const authCardInputBorderFocus = cn(
  "border border-[#E2E8F0] bg-white",
  "text-gray-900 placeholder:text-gray-400",
  "dark:border-[#E2E8F0] dark:bg-white",
  "focus:border-[#F3C7B5] focus:ring-0 focus:shadow-none",
  "focus-visible:border-[#F3C7B5] focus-visible:ring-0 focus-visible:shadow-none",
  "aria-invalid:border-red-500 aria-invalid:ring-0",
  authCardInputBorderFocusClassName,
);

const authSigninInputClass = cn(
  "h-11 rounded-[10px] px-4 py-2.5 text-base shadow-none md:text-sm",
  authCardInputBorderFocus,
);

/** Signup step 0 email: same idle/focus behavior as sign-in; radius ~8px per mockup. */
const authSignupEmailInputClass = cn(
  "h-11 rounded-lg px-4 py-2.5 text-base shadow-none md:text-sm",
  authCardInputBorderFocus,
);

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      icon: Icon,
      id,
      required,
      endAdornment,
      className,
      visualVariant = "default",
      ...props
    },
    ref,
  ) => {
    const labelBase =
      required && label.endsWith(" *") ? label.slice(0, -2).trimEnd() : label;

    const isAuthVariant = visualVariant !== "default";

    const labelClass = cn(
      "flex items-center gap-2",
      visualVariant === "default"
        ? "text-sm font-medium"
        : "text-sm font-bold text-gray-900",
    );

    const iconClass = cn(
      visualVariant === "default"
        ? "h-4 w-4 text-orange-500"
        : "h-[18px] w-[18px] shrink-0 text-[#f15a11]",
    );

    const defaultInputClass = cn(
      "focus:ring-2 focus:ring-orange-500",
      endAdornment && "pr-10",
    );

    const authInputClass = cn(
      visualVariant === "authSignin" ? authSigninInputClass : authSignupEmailInputClass,
      endAdornment && "pr-10",
    );

    const inputClass = cn(
      isAuthVariant ? authInputClass : defaultInputClass,
      className,
    );

    return (
      <div className="space-y-2">
        <Label htmlFor={id} className={labelClass}>
          <Icon
            className={iconClass}
            strokeWidth={visualVariant === "default" ? undefined : 2}
            aria-hidden
          />
          {labelBase}
          {required && <span className="text-red-500">*</span>}
        </Label>
        {endAdornment ? (
          <div className="relative">
            <Input
              id={id}
              ref={ref}
              required={required}
              className={inputClass}
              {...props}
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <span className="pointer-events-auto">{endAdornment}</span>
            </div>
          </div>
        ) : (
          <Input
            id={id}
            ref={ref}
            required={required}
            className={inputClass}
            {...props}
          />
        )}
      </div>
    );
  },
);

FormInput.displayName = "FormInput";

export default FormInput;
